import { useCallback, useContext, useMemo } from 'react';
import styled from '@emotion/styled';
import { createId } from '@paralleldrive/cuid2';
import type { Chart as IChart } from '@trinity/chat';
import type { Chat } from '@trinity/chat';
import { ChatAttachmentType } from '@trinity/chat';
import { AttachmentsContext } from '@trinity/chat-components/components/Chat/context';
import { Chart } from './Chart';
import { MessageTestingGridAttachment } from './MessageTestingGrid';
import { TableDataContainer } from './TableDataContainer';
import { ChartLegendContainer } from '../Chat/Message.Response.ChartContainer';
import { ChartVisualizationUI } from '../Widgets/ChartWidgetRouter';
import { KPIWidget } from '../Widgets/KPIWidget';
import { RepDashboardWidget } from '../Widgets/RepDashboardWidget';
import { RepDashboardSubmitQueryContext } from '../Widgets/RepDashboardSubmitQueryContext';
import { ChatMarkdown } from '../Markdown/Markdown';
import { useSubmitQueryMutation } from '../Chat/hooks/useSubmitQueryMutation';
import { useSelectedQueryParentIdentifier } from '../Chat/hooks/useSelectedQueryParentIdentifier';

type WidgetRepDashboardItemProps = {
  attachment: Chat.Attachment.WidgetRepDashboard;
};

const WidgetRepDashboardItem = ({ attachment }: WidgetRepDashboardItemProps) => {
  const { mutate } = useSubmitQueryMutation();
  const getSelectedQueryParentIdentifier = useSelectedQueryParentIdentifier();

  const handleSubmitQuery = useCallback(
    (query: string) => {
      mutate({
        identifier: createId(),
        parentQueryIdentifier: getSelectedQueryParentIdentifier(),
        query,
        action: null,
      });
    },
    [mutate, getSelectedQueryParentIdentifier]
  );

  return (
    <RepDashboardSubmitQueryContext.Provider value={handleSubmitQuery}>
      <RepDashboardWidget data={attachment.data} config={{ title: '', ...attachment.config }} />
    </RepDashboardSubmitQueryContext.Provider>
  );
};

type Props = {
  id: string;
};

export const Item = (props: Props) => {
  const attachments = useContext(AttachmentsContext);
  const attachment = useMemo(() => attachments.find((x) => x.id === props.id), [attachments, props.id]);

  switch (attachment?.type) {
    case ChatAttachmentType.Chart: {
      const { values, ...other } = attachment.data;

      const item = {
        ...other,
        data: values,
      } as IChart['chart'];

      const defaultLegendOpen = item.legend?.defaultOpen ?? false;

      return (
        <ChartLegendContainer defaultValue={defaultLegendOpen}>
          <Chart item={item} type={attachment.data.type} />
        </ChartLegendContainer>
      );
    }

    case ChatAttachmentType.Table: {
      return <TableDataContainer attachment={attachment} />;
    }

    case ChatAttachmentType.WidgetChart: {
      return (
        <ChartWidgetContainer>
          <ChartVisualizationUI chartData={attachment.data} colors={attachment.colors} />
        </ChartWidgetContainer>
      );
    }

    case ChatAttachmentType.WidgetText: {
      return <ChatMarkdown value={attachment.data.content} />;
    }

    case ChatAttachmentType.WidgetMetricCard: {
      return (
        <KPIWidget
          title={attachment.title ?? ''}
          backgroundColor={attachment.backgroundColor ?? '#ffffff'}
          values={attachment.data.values}
          labels={attachment.data.labels}
        />
      );
    }

    case ChatAttachmentType.WidgetRepDashboard: {
      return <WidgetRepDashboardItem attachment={attachment} />;
    }

    case ChatAttachmentType.MessageTestingGrid: {
      return <MessageTestingGridAttachment id={props.id} />;
    }

    default: {
      console.error(`Unsupported attachment type: ${attachment?.type}`);
      return null;
    }
  }
};

const ChartWidgetContainer = styled('div')({
  minHeight: 300,
});

Item.displayName = 'Chat.Attachments.Item';
